import { useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { FormControl, FormHelperText, InputBase, InputLabel, InputBaseProps } from '@mui/material';
// import { InputBase } from '@mui/material/TextField';
import { useField, useFormikContext } from 'formik';
import { values } from 'lodash';
import { Box } from '@mui/system';

export const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(4),
  },
  '&.MuiInputBase-root': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #DDEAF3',
    // transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '.MuiInputBase-input': {
      margin: '5px 12px',
      borderRadius: 4,
      // '&:focus': {
      //   boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      //   borderColor: theme.palette.primary.main,
      // },
      backgroundColor: 'transparent !important',
    },
    '& input': {
      backgroundColor: 'transparent !important',
    },
    '& input:hover': {
      backgroundColor: 'transparent !important',
    },
    '& input:focus': {
      backgroundColor: 'transparent !important',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px white inset !important',
      WebkitTextFillColor: '#000',
      backgroundColor: 'transparent !important',
    },
    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 1000px white inset !important',
      WebkitTextFillColor: '#000',
      backgroundColor: 'transparent !important',
    },
    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: '0 0 0 1000px white inset !important',
      backgroundColor: 'transparent !important',
    },
    '.MuiInputAdornment-root': {
      marginLeft: '12px',
      marginRight: 0,
      paddingRight: '12px',
      borderRight: '1px solid #DDEAF3',
      height: '45px',
      '.MuiCardMedia-root': {
        width: 24,
      },
    },
    '& ::placeholder': {
      textTransform: 'none',
      fontSize: '0.9rem',
    },
    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    'input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
}));

export default function MFTextField({
  name,
  label,
  placeholder,
  defaultValue,
  readonly,
  inputProps,
  margin,
  startAdornment,
  onChange,
  sx,
  trimOnBlur = true,
  displayCharecterCounter = '',
  applyMarginBottom = true,
  onlyAlphabets = false,
  allowSpaces = true,
  collapseSpaces = false,
  blockLeadingSpace = false,
  ...props
}: InputBaseProps & {
  name: string;
  label?: string;
  margin?: 'dense' | 'none' | undefined;
  placeholder: string;
  defaultValue?: string;
  readonly?: boolean;
  startAdornment?: JSX.Element;
  trimOnBlur?: boolean;
  displayCharecterCounter?: string;
  applyMarginBottom?: boolean;
  onlyAlphabets?: boolean;
  allowSpaces?: boolean;
  collapseSpaces?: boolean;
  blockLeadingSpace?: boolean;
}): JSX.Element {
  const id = `${name}-input`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext<any>();
  const { setTouched } = helpers;
  const errorText = meta.error && meta.touched ? meta.error : '';

  // fallback to Formik field.onChange if no custom onChange provided
  const effectiveOnChange = onChange || field.onChange;

  const stripNonAlphabet = (s: string) => {
    const regex = allowSpaces ? /[^A-Za-z\s]/g : /[^A-Za-z]/g;
    return s.replace(regex, '');
  };

  const sanitizeSpaces = (s: string) => {
    if (!allowSpaces) {
      return s.replace(/\s+/g, '');
    }
    let out = s.replace(/^\s+/, '');
    if (collapseSpaces) out = out.replace(/\s{2,}/g, ' ');
    return out;
  };

  const sanitizeValue = (raw: string) => {
    let next = raw;
    if (onlyAlphabets) next = stripNonAlphabet(next);
    next = sanitizeSpaces(next);
    return next;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!allowSpaces) {
      if (e.key === ' ') {
        e.preventDefault();
      }
      return;
    }
    if (!blockLeadingSpace) return;

    const key = e.key;
    const target = e.target as HTMLInputElement;

    if (key === ' ' && (target.selectionStart ?? 0) === 0) {
      e.preventDefault();
      return;
    }
    if (key === ' ') {
      const left = target.value.slice(0, target.selectionStart ?? 0);
      if (left.endsWith(' ')) {
        e.preventDefault();
      }
    }
  };

  const handleInternalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeValue(e.target.value ?? '');
    const synthetic = {
      ...e,
      target: { ...e.target, value: sanitized, name },
    } as unknown as React.ChangeEvent<any>;
    effectiveOnChange(synthetic);
  };

  // eslint-disable-next-line
  const onBlur = (e: React.ChangeEvent<any>): void => {
    const { target: fieldTarget } = e;
    let { value } = fieldTarget;
    value = sanitizeSpaces(value ?? '')

    setTimeout(() => {
      trimOnBlur &&
        setFieldValue(
          name,
          value && typeof value === 'string'
            ? !meta.initialValue && value.trim() === ''
              ? meta.initialValue
              : value.trim()
            : value
        );
      setTouched(true, true);
    }, 0);
  };

  useEffect(() => {
    inputRef.current?.addEventListener('wheel', (e) => e.preventDefault());
  }, []);

  return (
    <FormControl
      variant="outlined"
      fullWidth
      sx={{ mt: { xs: 1, md: 2 }, mb: applyMarginBottom ? { xs: 1, md: 2 } : 1, ...sx }}>
      {label && (
        <InputLabel
          shrink
          htmlFor={id}
          sx={{
            transform: 'unset',
            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
          }}>
          {label}
        </InputLabel>
      )}
      <BootstrapInput
        defaultValue={defaultValue}
        placeholder={placeholder}
        id={id}
        startAdornment={startAdornment}
        {...field}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        onChange={handleInternalChange}
        margin={margin}
        disabled={readonly || false}
        inputProps={inputProps}
        {...props}
        ref={inputRef}
        sx={{
          '& .Mui-disabled': {
            cursor: !props.disabled ? 'text' : 'not-allowed !important',
            pointerEvents: 'all',
          },
          ...sx,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: errorText ? 'row' : 'row-reverse',
        }}>
        {errorText && (
          <FormHelperText error sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
        {displayCharecterCounter && (
          <FormHelperText sx={{ marginLeft: 'unset' }}>
            {(field.value ?? '').toString().length} / {displayCharecterCounter}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
}
